import { SalesforcePipeline, SalesforcePipelineKind } from "../schema/salesforceDeal";

/**
 * Which id space a pipeline id belongs to, read off the pipeline the backend returned.
 *
 * Deliberately **not** derived from the id's shape the way the Dynamics helper has to derive
 * its own: `getPipelines` carries `pipelineKind` on every entry precisely so no consumer has
 * to guess, and a synthesised pipeline's id is an opaque string that could collide with a
 * real record-type id under any shape rule.
 */
export const pipelineKindOf = (
	pipelines: SalesforcePipeline[],
	pipelineId: string | null | undefined
): SalesforcePipelineKind | null =>
	pipelines.find((pipeline) => pipeline.id === pipelineId)?.pipelineKind ?? null;

/** Whether the org runs no Opportunity record types, so stages come from the plain picklist. */
export const isSynthesisedPipeline = (
	pipelines: SalesforcePipeline[],
	pipelineId: string | null | undefined
): boolean => pipelineKindOf(pipelines, pipelineId) === SalesforcePipelineKind.DEFAULT;

/**
 * The value that may actually be written to `RecordTypeId`.
 *
 * A `DEFAULT` pipeline is Signalist's own invention for an org with no Opportunity record
 * types — its id exists so the picker has something to select and is **never** written. Saving
 * it would put a made-up id into `RecordTypeId` and the org would reject the Opportunity, so
 * the config stores `null` instead.
 */
export const resolveWritablePipelineId = (
	pipelines: SalesforcePipeline[],
	pipelineId: string | null | undefined
): string | null => {
	if (!pipelineId || isSynthesisedPipeline(pipelines, pipelineId)) {
		return null;
	}

	return pipelines.some((pipeline) => pipeline.id === pipelineId) ? pipelineId : null;
};

/**
 * The stage id that may stay selected for `pipelineId`.
 *
 * A record type selects the Sales Process and therefore which `StageName` values are legal, so
 * a stage carried over from another record type would be rejected on write. Returns `null`
 * whenever the stored stage does not belong to the selected pipeline — including when that
 * pipeline has disappeared from the org entirely.
 */
export const resolveSelectableStageId = (
	pipelines: SalesforcePipeline[],
	pipelineId: string | null | undefined,
	stageId: string | null | undefined
): string | null => {
	if (!stageId || !pipelineId) {
		return null;
	}

	const pipeline = pipelines.find((candidate) => candidate.id === pipelineId);

	return pipeline?.stages.some((stage) => stage.id === stageId) ? stageId : null;
};

/** The stages of the selected pipeline, in the order the org's sales process defines. */
export const stagesOfPipeline = (
	pipelines: SalesforcePipeline[],
	pipelineId: string | null | undefined
): SalesforcePipeline["stages"] => {
	const stages = pipelines.find((pipeline) => pipeline.id === pipelineId)?.stages ?? [];

	// `displayOrder` is optional; a stable sort keeps describe's own order when it is absent.
	return [...stages].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
};
